import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useActiveFamily } from '../context/ActiveFamilyContext';
import { useMemberRole } from './useMemberRole';

/**
 * Hook que verifica si el owner del grupo tiene el plan vencido
 * Solo relevante para miembros que NO son owner
 */
export function useOwnerPlanStatus() {
  const { user } = useAuth();
  const { activeFamily } = useActiveFamily();
  const { isOwner } = useMemberRole();
  
  const [ownerPlanExpired, setOwnerPlanExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ownerName, setOwnerName] = useState<string>('');

  useEffect(() => {
    checkOwnerPlan();
  }, [activeFamily?.id, user?.uid, isOwner]);

  const checkOwnerPlan = async () => {
    // Si no hay familia activa o el usuario es el owner, no verificar
    if (!activeFamily?.id || !activeFamily?.ownerId || !user?.uid || isOwner) {
      setOwnerPlanExpired(false);
      setLoading(false);
      return;
    }

    // Si el usuario es el owner, no mostrar nada
    if (activeFamily.ownerId === user.uid) {
      setOwnerPlanExpired(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Obtener información del owner (ya validamos que ownerId existe)
      const ownerId = activeFamily.ownerId;
      const ownerDoc = await getDoc(doc(db, 'users', ownerId));
      
      if (!ownerDoc.exists()) {
        setOwnerPlanExpired(false);
        return;
      }

      const ownerData = ownerDoc.data();
      const ownerPlan = ownerData.subscription?.plan || 'free';
      
      // Obtener información del member para ver el nombre del owner
      const memberDoc = await getDoc(doc(db, 'members', ownerId));
      if (memberDoc.exists()) {
        setOwnerName(memberDoc.data().name || '');
      }

      // ⭐ VERIFICAR: Si el owner tiene plan FREE, significa que venció
      // (asumiendo que antes tenía un plan pago)
      setOwnerPlanExpired(ownerPlan === 'free');
      
    } catch (error) {
      console.error('Error verificando plan del owner:', error);
      setOwnerPlanExpired(false);
    } finally {
      setLoading(false);
    }
  };

  return {
    ownerPlanExpired,
    loading,
    ownerName,
    isOwner, // Para saber si debe mostrarse el wizard o el modal de miembro
  };
}
